import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronLeft, Check, Shield, Wallet, Receipt, ArrowLeft, ShoppingCart, CreditCard, Lock, Zap, Clock, DollarSign, HelpCircle, ChevronRight, Globe, RefreshCw, X, Mail } from 'lucide-react';
import { getProductMainImage } from '../config/productImages';
import { homeProductsConfig, getHomeProductImage } from '../config/homeProductsConfig';
import { submitGuestOrder, checkOrderStatus } from '../api/productApi';
import { getProductFAQ } from '../config/productDetailConfigI18n';
import { autoLoginWithEmail } from '../store/userSlice';

const OrderConfirmationPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector((state) => state.user);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('alipay');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(''); // 保存支付链接
  const [showManualRedirect, setShowManualRedirect] = useState(false); // 显示手动跳转按钮
  const [orderNumber, setOrderNumber] = useState(''); // 保存订单号
  const [isPolling, setIsPolling] = useState(false); // 轮询状态
  const pollingIntervalRef = useRef(null); // 轮询定时器引用
  const [expandedFAQ, setExpandedFAQ] = useState(null); // 控制FAQ展开状态
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false); // 防止重复自动登录
  const [showPaymentMethodAlert, setShowPaymentMethodAlert] = useState(false); // 控制支付方式提醒显示
  const [copySuccess, setCopySuccess] = useState(''); // 复制成功状态
  const [showToast, setShowToast] = useState(false); // 显示提示状态
  const [displayProductName, setDisplayProductName] = useState(''); // 产品显示名称

  // 复制文本到剪贴板
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setShowToast(true);
      setTimeout(() => {
        setCopySuccess('');
        setShowToast(false);
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 从URL参数中获取订单信息
  const urlEmail = searchParams.get('email') || 'user@example.com';
  const urlProductId = searchParams.get('productId') || '';
  const urlQuantity = parseInt(searchParams.get('quantity')) || 1;
  const urlVersion = searchParams.get('version') || '';
  const urlProductName = searchParams.get('productName') || t('orderConfirmation.defaultProductName');
  const urlAccessToken = searchParams.get('accessToken') || ''; // 获取accessToken参数
  
  // 调试：检查翻译功能
  useEffect(() => {
    console.log('=== 翻译系统调试 ===');
    console.log('当前语言:', i18n.language);
    console.log('原始产品名称:', urlProductName);
    console.log('测试基础翻译 - orderConfirmation.title:', t('orderConfirmation.title'));
    console.log('测试SKU翻译 - sku.official.1user.12months.name:', t('sku.official.1user.12months.name'));
    console.log('测试SKU翻译 - sku.officialShared.3users.1month.name:', t('sku.officialShared.3users.1month.name'));
    console.log('直接翻译目标key:', t(urlProductName));
    
    // 检查翻译资源是否正确加载
    console.log('检查翻译资源:');
    console.log('i18n资源包:', i18n.getResourceBundle(i18n.language, 'translation'));
    console.log('检查sku部分:', i18n.getResourceBundle(i18n.language, 'translation')?.sku);
    console.log('检查official部分:', i18n.getResourceBundle(i18n.language, 'translation')?.sku?.official);
    
    // 直接访问翻译数据
    const resources = i18n.getResourceBundle(i18n.language, 'translation');
    if (resources?.sku?.official?.['1user']?.['12months']?.name) {
      console.log('找到翻译:', resources.sku.official['1user']['12months'].name);
    } else {
      console.log('❌ 翻译路径不存在');
    }
  }, [urlProductName, t, i18n]);
  
  // 直接进行产品名称翻译
  useEffect(() => {
    let newDisplayName = urlProductName;
    
    if (urlProductName.startsWith('sku.')) {
      const translatedName = t(urlProductName);
      console.log('产品名称翻译:');
      console.log('- 语言:', i18n.language);
      console.log('- key:', urlProductName);
      console.log('- 翻译结果:', translatedName);
      
      newDisplayName = translatedName;
    }
    
    setDisplayProductName(newDisplayName);
  }, [urlProductName, t, i18n.language]);
  const urlProductType = searchParams.get('productType') || 'default';
  const urlPrice = parseFloat(searchParams.get('price')) || 0;
  const urlOriginalPrice = parseFloat(searchParams.get('originalPrice')) || 0;

  // 根据URL参数构建订单数据
  // 优先从首页配置获取图片，保持与产品详情页一致的逻辑
  const productImage = (() => {
    console.log('=== 产品图片选择调试 ===');
    console.log('产品ID (urlProductId):', urlProductId);
    console.log('产品类型 (urlProductType):', urlProductType);
    console.log('用户配置 (urlUserConfig):', searchParams.get('userConfig'));
    
    // 首先尝试从首页配置获取图片
    const homeImage = getHomeProductImage(urlProductId);
    if (homeImage) {
      console.log('✅ 订单页面使用首页配置的图片:', homeImage, '产品ID:', urlProductId);
      return homeImage;
    }
    
    // 如果没找到配置的图片，但是是共享账户类型，使用智能匹配
    if (urlProductType === 'official-shared') {
      const userConfig = searchParams.get('userConfig');
      console.log('🔍 检测到共享账户类型，用户配置:', userConfig);
      
      if (userConfig === '3users') {
        console.log('✅ 使用3人共享的图片');
        return '/images/gpt/3.jpg';
      } else if (userConfig === '6users') {
        console.log('✅ 使用6人共享的图片');
        return '/images/gpt/6.jpg';
      }
    }
    
    // 如果没找到，使用原来的逻辑作为备用方案
    console.log('⚠️ 未找到产品ID对应的图片配置，使用默认图片逻辑');
    console.log('产品类型:', urlProductType);
    const defaultImage = getProductMainImage(urlProductType);
    console.log('默认图片:', defaultImage);
    return defaultImage;
  })();
  
  const orderData = {
    product: {
      id: urlProductId,
      name: displayProductName,
      version: urlVersion,
      type: urlProductType,
      price: urlPrice,
      originalPrice: urlOriginalPrice,
      quantity: urlQuantity,
      image: productImage
    },
    customer: {
      email: urlEmail
    },
    shipping: {
      method: t('orderConfirmation.shipping.standard'),
      fee: 0,
      estimatedDays: "3-5"
    }
  };

  // 支付方式配置 - 只保留支付宝和支付宝香港
  const paymentMethods = {
    all: [
      { id: 'alipay', name: '支付宝', icon: '/images/payment/16.jpg', description: t('orderConfirmation.payment.alipayDesc'), channel: 1 },
      { id: 'alipay_hk', name: '支付宝香港', icon: '/images/payment/17.jpg', description: t('orderConfirmation.payment.alipayHkDesc'), channel: 2 },
      { id: 'credit_card', name: '信用卡', icon: '/images/payment/18.jpg', description: t('orderConfirmation.payment.creditCardDesc'), channel: 10 }
    ]
  };

  const subtotal = orderData.product.price * orderData.product.quantity;
  const total = subtotal;

  const handlePayment = async () => {
    if (!selectedPaymentMethod) {
      // 显示友好的提醒而不是alert
      setShowPaymentMethodAlert(true);
      // 3秒后自动隐藏提醒
      setTimeout(() => {
        setShowPaymentMethodAlert(false);
      }, 3000);
      return;
    }


    console.log('=== 开始支付流程 ===');
    console.log('AccessToken:', urlAccessToken ? '已获取' : '未获取');
    setPaymentLoading(true);

    try {
      // 获取选中的支付方式详情
      const selectedPaymentDetails = paymentMethods.all.find(
        method => method.id === selectedPaymentMethod
      );

      if (!selectedPaymentDetails) {
        throw new Error(t('orderConfirmation.errors.paymentMethodNotFound'));
      }

      console.log('选中的支付方式:', selectedPaymentDetails);

      // 准备支付请求数据
      const paymentData = {
        uid: urlEmail, // 用户邮箱作为uid
        product_id: urlProductId, // 产品ID
        channel: selectedPaymentDetails.channel, // 支付渠道
        domain: window.location.hostname, // 当前访问的主域名
        // 可以添加其他必要参数
        amount: orderData.product.price,
        quantity: orderData.product.quantity,
        version: orderData.product.version,
        productName: displayProductName,
        productType: orderData.product.type,
        accessToken: urlAccessToken // 添加accessToken参数
      };

      console.log('支付请求数据:', paymentData);

      // 统一使用游客支付接口
      console.log('使用submitGuestOrder API进行支付');
      const response = await submitGuestOrder(paymentData);

      console.log('支付API返回:', response);

      // 检查API返回结果
      if (response && response.data) {
        const { payUrl, out_trade_no, returnUrl } = response.data;
        
        console.log('支付链接:', payUrl);
        console.log('订单号:', out_trade_no);
        console.log('返回链接:', returnUrl);

        if (payUrl) {
          // 保存支付链接和订单号
          setPaymentUrl(payUrl);
          setOrderNumber(out_trade_no);
          
          // 直接打开新标签页
          window.open(payUrl, '_blank');

          // 显示手动跳转按钮
          setShowManualRedirect(true);
        } else {
          throw new Error(t('orderConfirmation.errors.emptyPaymentUrl'));
        }
      } else {
        throw new Error(t('orderConfirmation.errors.invalidApiResponse'));
      }

    } catch (error) {
      console.error('支付失败:', error);
      alert(`${t('orderConfirmation.errors.paymentFailed')}: ${error.message}`);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const startPolling = async (orderNo) => {
    setIsPolling(true);
    console.log('开始轮询订单状态，订单号:', orderNo);
    
    try {
      const response = await checkOrderStatus(orderNo);
      console.log('订单状态查询结果:', response);
      
      if (response && response.data) {
        const { pay_result } = response.data;
        
        if (pay_result === 'success') {
          // 支付成功
          console.log('🎉 支付完成！开始处理自动登录和跳转');
          setIsPolling(false);
          clearTimeout(pollingIntervalRef.current);
          
          // 如果用户未登录且有邮箱，先进行自动登录
          if (!isLoggedIn && !autoLoginAttempted && urlEmail) {
            console.log('💡 检测到未登录用户，使用订单邮箱进行自动登录:', urlEmail);
            setAutoLoginAttempted(true);
            
            try {
              const result = await dispatch(autoLoginWithEmail(urlEmail));
              
              if (autoLoginWithEmail.fulfilled.match(result)) {
                console.log('✅ 订单页面自动登录成功:', result.payload);
                // 登录成功后再跳转
                setTimeout(() => {
                  navigate(`/detail/success?orderId=${orderNo}&productType=${urlProductType}&productName=${encodeURIComponent(displayProductName)}&price=${urlPrice}&version=${encodeURIComponent(urlVersion)}`);
                }, 500); // 给一点时间让状态更新
              } else {
                console.log('❌ 订单页面自动登录失败，直接跳转:', result.payload);
                // 登录失败也要跳转
                navigate(`/detail/success?orderId=${orderNo}&productType=${urlProductType}&productName=${encodeURIComponent(displayProductName)}&price=${urlPrice}&version=${encodeURIComponent(urlVersion)}`);
              }
            } catch (error) {
              console.error('💥 自动登录过程中发生错误，直接跳转:', error);
              // 出错也要跳转
              navigate(`/detail/success?orderId=${orderNo}&productType=${urlProductType}&productName=${encodeURIComponent(displayProductName)}&price=${urlPrice}&version=${encodeURIComponent(urlVersion)}`);
            }
          } else {
            console.log('🔄 用户已登录或已尝试过自动登录，直接跳转到成功页面');
            // 用户已登录或已尝试过自动登录，直接跳转
            navigate(`/detail/success?orderId=${orderNo}&productType=${urlProductType}&productName=${encodeURIComponent(urlProductName)}&price=${urlPrice}&version=${encodeURIComponent(urlVersion)}`);
          }
          return;
        } else if (pay_result === 'unkonw') {
          // 支付未完成，继续轮询
          console.log('支付未完成，继续轮询。当前状态:', pay_result);
          pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
        } else {
          // 其他状态，继续轮询
          console.log('订单状态未知，继续轮询。当前状态:', pay_result);
          pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
        }
      } else {
        console.log('订单状态查询返回数据异常，继续轮询');
        // 继续轮询
        pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
      }
    } catch (error) {
      console.error('支付状态轮询失败:', error);
      // 发生错误时也继续轮询，避免因网络问题中断
      pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
    }
  };

  // 当有订单号和支付链接时启动轮询
  useEffect(() => {
    if (orderNumber && paymentUrl && !isPolling) {
      console.log('启动订单状态轮询');
      startPolling(orderNumber);
    }
    
    // 清理定时器
    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    };
  }, [orderNumber, paymentUrl]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50">
      
      {/* 优化的顶部导航 - 更加精致 */}
      <div className="bg-white/90 backdrop-blur-md border-b border-purple-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all duration-200 font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('orderConfirmation.buttons.back')}</span>
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-red-600 bg-clip-text text-transparent">
                {t('orderConfirmation.title')}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Shield className="w-4 h-4 text-green-500" />
              <span>{t('orderConfirmation.security.title')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* 左侧 - 订单摘要和FAQ (占3/5空间) */}
          <div className="lg:col-span-3 space-y-6">
            {/* 订单摘要卡片 - 优化设计 */}
            <div className="bg-gradient-to-br from-purple-50 via-white to-amber-50 rounded-3xl shadow-2xl border border-purple-200 overflow-hidden relative">
              {/* 装饰性背景元素 */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-amber-200 rounded-full opacity-20 blur-3xl"></div>
              
              <div className="relative z-10 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Receipt className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{t('orderConfirmation.summary.title')}</h3>
                </div>
                
                {/* 产品信息 - 优化样式，产品名称单独一行 */}
                <div className="mb-6 p-5 bg-white rounded-2xl shadow-sm border border-purple-100">
                  {/* 产品名称 - 单独一行，完整显示 */}
                  <div className="mb-4">
                    <h4 className="font-bold text-gray-900 leading-tight text-base">
                      {orderData.product.name}
                    </h4>
                  </div>
                  
                  {/* 产品图片、详情和价格 */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-amber-100 rounded-xl overflow-hidden flex-shrink-0 shadow-md">
                      <img 
                        src={orderData.product.image} 
                        alt={orderData.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-purple-600 text-sm font-semibold">{orderData.product.version}</p>
                      <p className="text-gray-600 text-sm mt-1">{t('orderConfirmation.summary.quantity')}: {orderData.product.quantity}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-red-500 bg-clip-text text-transparent">
                        ${orderData.product.price}
                      </div>
                      {orderData.product.originalPrice > orderData.product.price && (
                        <div className="text-gray-400 line-through text-sm">
                          ${orderData.product.originalPrice}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* 收货邮件 - 优化样式 */}
                <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200">
                  <div className="text-center">
                    <div className="text-sm text-blue-700 font-semibold mb-2 flex items-center justify-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      {t('orderConfirmation.summary.shippingEmail')}
                    </div>
                    <div className="font-bold text-gray-900 break-all">
                      {orderData.customer.email}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 重要声明和FAQ部分 */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6 lg:p-8">
                {/* 重要购买声明 */}
                <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 via-amber-50 to-yellow-50 border-l-4 border-purple-400 rounded-lg shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-purple-800 mb-2">重要声明</h4>
                      <p className="text-sm text-purple-700 leading-relaxed">
                        本产品支持<span className="font-semibold text-purple-800">免费试用</span>，我们不对模型的输出质量（包括GPT降智等现象）负责。
                        产品一旦购买即视为接受此条款，<span className="font-semibold text-purple-800">不退不换</span>，感谢理解。
                      </p>
                      <div className="mt-2 text-xs text-purple-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        建议您在购买前先进行免费试用，确认产品符合您的需求
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {/* 动态渲染FAQ问答项 - 可折叠版本 */}
                  {getProductFAQ(urlProductType).slice(0, 5).map((faq, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-purple-300 hover:shadow-md">
                      <button
                        onClick={() => {
                          if (expandedFAQ === 'all') {
                            setExpandedFAQ(index);
                          } else {
                            setExpandedFAQ(expandedFAQ === index ? null : index);
                          }
                        }}
                        className="w-full flex items-start gap-3 p-4 text-left hover:bg-purple-50 transition-all duration-300"
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-red-500 flex items-center justify-center shadow-md transition-transform duration-300 ${
                            expandedFAQ === index || expandedFAQ === 'all' ? 'rotate-90' : ''
                          }`}>
                            <ChevronRight className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-gray-900 flex items-center justify-between gap-2 group">
                            <span>{faq.question}</span>
                            <span className="text-xs text-gray-400 group-hover:text-purple-500 transition-colors duration-200">
                              {(expandedFAQ === index || expandedFAQ === 'all') ? t('orderConfirmation.faq.collapse') : t('orderConfirmation.faq.expand')}
                            </span>
                          </h4>
                        </div>
                      </button>
                      
                      {/* 可折叠的答案部分 */}
                      <div className={`overflow-hidden transition-all duration-300 ${
                        expandedFAQ === index || expandedFAQ === 'all' ? 'max-h-96' : 'max-h-0'
                      }`}>
                        <div className="px-4 pb-4 pl-12">
                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border-l-4 border-purple-400">
                            <p className="text-xs text-gray-700 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 联系客服 */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 text-center">
                    {t('orderConfirmation.faq.contact')}
                    <a href="#" className="text-purple-500 hover:text-purple-600 font-semibold ml-1 inline-flex items-center gap-1 group">
                      {t('orderConfirmation.buttons.contact')}
                      <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧 - 支付方式选择 (占2/5空间) */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-2xl border border-purple-100 overflow-hidden sticky top-24">
              <div className="p-6 space-y-5">
                <h2 className="text-lg font-semibold text-gray-900">选择支付渠道</h2>
                {/* 支付方式列表 - 精简布局 */}
                <div className="space-y-3">
                  {paymentMethods.all.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                      className={`group w-full p-4 rounded-2xl text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 relative overflow-hidden ${
                        selectedPaymentMethod === method.id
                          ? 'border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-amber-50 shadow-lg ring-2 ring-purple-100'
                          : 'border border-gray-200 hover:border-purple-300 bg-white'
                      }`}
                    >
                      {/* 装饰性背景 */}
                      {selectedPaymentMethod === method.id && (
                        <div className="absolute -top-10 -right-10 w-20 h-20 bg-purple-200 rounded-full opacity-20 blur-2xl"></div>
                      )}

                      <div className="relative z-10">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex-shrink-0">
                              <img
                                src={method.icon}
                                alt={method.name}
                                className="w-full h-full object-contain rounded-lg"
                              />
                            </div>
                            <span className="font-semibold text-gray-900 text-base">{method.name}</span>
                          </div>
                          {selectedPaymentMethod === method.id && (
                            <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-red-500 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* 价格明细 - 优化样式 */}
                <div className="space-y-3 p-4 bg-white rounded-2xl border border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t('orderConfirmation.summary.subtotal')}</span>
                    <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* 总计 - 更突出的设计 */}
                <div className="p-4 bg-gradient-to-r from-purple-100 to-amber-100 rounded-2xl border border-purple-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-lg font-bold text-gray-900">{t('orderConfirmation.summary.total')}</span>
                    </div>
                    <span className="text-3xl font-bold bg-gradient-to-r from-purple-500 to-red-500 bg-clip-text text-transparent">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* 支付按钮 - 始终高亮设计 */}
                <button
                  onClick={handlePayment}
                  disabled={paymentLoading}
                  className={`mt-6 relative w-full bg-gradient-to-r from-purple-500 via-purple-600 to-red-500 text-white py-5 rounded-2xl font-bold text-lg shadow-2xl flex items-center justify-center gap-3 transition-all duration-300 overflow-hidden group ${
                    !paymentLoading
                      ? 'hover:scale-105 hover:shadow-3xl transform'
                      : 'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                  }`}
                >
                  {/* 动态背景效果 */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-red-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* 按钮内容 */}
                  <div className="relative z-10 flex items-center gap-3">
                    {paymentLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{t('orderConfirmation.buttons.processing')}</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
                        <span>{t('orderConfirmation.buttons.pay')} ${total.toFixed(2)}</span>
                        <span className="text-2xl group-hover:translate-x-1 transition-transform duration-300">→</span>
                      </>
                    )}
                  </div>

                  {/* 闪光效果 - 始终显示 */}
                  {!paymentLoading && (
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 via-red-400 to-purple-400 opacity-30 blur-lg group-hover:opacity-50 transition-opacity duration-300"></div>
                  )}
                </button>

                {/* 支付方式选择提醒 */}
                {showPaymentMethodAlert && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-red-50 to-purple-50 border-2 border-red-200 rounded-2xl animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-lg">⚠️</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-red-800 text-sm">
                          {t('orderConfirmation.alerts.selectPaymentMethodTitle')}
                        </p>
                        <p className="text-red-600 text-xs mt-1">
                          {t('orderConfirmation.alerts.selectPaymentMethod')}
                        </p>
                      </div>
                      <button
                        onClick={() => setShowPaymentMethodAlert(false)}
                        className="w-6 h-6 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors duration-200"
                      >
                        <X className="w-3 h-3 text-red-500" />
                      </button>
                    </div>
                  </div>
                )}

                {/* 安全提示 - 更突出的设计 */}
                <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <Shield className="w-4 h-4 text-green-500" />
                    <span>{t('orderConfirmation.security.title')}</span>
                  </div>
                  <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  <div className="flex items-center gap-1">
                    <Lock className="w-4 h-4 text-blue-500" />
                    <span>{t('orderConfirmation.security.ssl')}</span>
                  </div>
                  <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  <div className="flex items-center gap-1">
                    <RefreshCw className="w-4 h-4 text-purple-500" />
                    <span>{t('orderConfirmation.security.warranty')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 居中的手动跳转提醒框 */}
      {showManualRedirect && paymentUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform relative">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowManualRedirect(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200 group"
            >
              <X className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-purple-500" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                💳 {t('orderConfirmation.payment.ready')}
              </h3>
              
              <p className="text-gray-600 text-sm mb-6">
                {t('orderConfirmation.payment.manualRedirect')}
              </p>
              
              {/* 轮询状态显示 */}
              {isPolling && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-blue-700 text-sm font-medium">
                      {t('orderConfirmation.payment.checking')}
                    </span>
                  </div>
                  <p className="text-blue-600 text-xs mt-1 text-center">
                    {t('orderConfirmation.payment.completedAuto')}
                  </p>
                </div>
              )}
              
              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = paymentUrl}
                  className="w-full bg-gradient-to-r from-purple-500 to-red-500 text-white py-4 rounded-xl font-bold hover:from-purple-600 hover:to-red-600 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg transform hover:scale-105"
                >
                  <Receipt className="w-5 h-5" />
                  {t('orderConfirmation.buttons.manualOpen')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast 通知 - 复制成功反馈 */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-out">
          <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
            <Check className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium">
              复制成功！
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderConfirmationPage;
